/**
 * BestLineBadge Component
 * Shows a subtle badge when a better line exists at another sportsbook.
 * Only renders when there IS a better line — no badge = user has the best or only line.
 */

import { SPORTSBOOKS } from '@config/constants';
import type { BestOddsResult } from '@parlay-picker/shared/oddsUtils';
import {
  getBookDisplayName,
  getShortBookName,
  getSportsbookIdByOddsApiKey,
} from '@parlay-picker/shared/sportsbook';
import type { MouseEvent, ReactElement } from 'react';

interface BestLineBadgeProps {
  comparison: BestOddsResult;
  /** When provided, the badge becomes clickable (opens comparison modal) */
  onClick?: (e: MouseEvent) => void;
}

export function BestLineBadge({ comparison, onClick }: BestLineBadgeProps): ReactElement | null {
  if (!comparison.hasBetterLine) return null;

  const bestKey = comparison.bestBook.sportsbook;
  const bookConfig = SPORTSBOOKS[getSportsbookIdByOddsApiKey(bestKey) ?? bestKey];
  const shortName = getShortBookName(bestKey);
  const dotColor = bookConfig?.dot || 'bg-theme-success';

  const badge = (
    <span
      className={`inline-flex items-center gap-1 text-xs bg-theme-success/15 text-theme-success px-1.5 py-0.5 rounded${
        onClick ? ' cursor-pointer hover:bg-theme-success/25 transition-colors' : ''
      }`}
      title={`Best odds: ${comparison.bestBook.odds} at ${getBookDisplayName(bestKey)}. You have: ${comparison.currentBook.odds}`}
      onClick={onClick}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
      <span>{comparison.bestBook.odds}</span>
      <span className="text-theme-success/70">{shortName}</span>
    </span>
  );

  return badge;
}
